import GavelIcon from "@mui/icons-material/Gavel";
import SyncIcon from "@mui/icons-material/Sync";
import { Alert, Button, Chip, Stack, Typography } from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useCallback } from "react";
import { useFragment } from "react-relay";
import { InternAgreementsCard_brand$key } from "./__generated__/InternAgreementsCard_brand.graphql";
import useSyncAppleDeveloperAgreementsMutation from "../../../mutations/useSyncAppleDeveloperAgreementsMutation";
import { InternPanel } from "../components";

const brandFragment = graphql`
  fragment InternAgreementsCard_brand on BrandType {
    id
    appleDeveloperAgreementsPendingSince
    showIosTosAlert
  }
`;

type Props = {
  brand: InternAgreementsCard_brand$key;
};

const InternAgreementsCard = ({ brand: brandKey }: Props) => {
  const brand = useFragment(brandFragment, brandKey);

  const [syncMutation, isSyncInFlight] =
    useSyncAppleDeveloperAgreementsMutation(brand.id);

  const handleSync = useCallback(() => {
    syncMutation();
  }, [syncMutation]);

  const hasPendingAgreements =
    brand.appleDeveloperAgreementsPendingSince != null;
  const showTosAlert = brand.showIosTosAlert;

  const getStatusColor = (): "error" | "warning" | "success" => {
    if (hasPendingAgreements) return "error";
    if (showTosAlert) return "warning";
    return "success";
  };

  const getStatusText = () => {
    if (hasPendingAgreements) return "Pending";
    if (showTosAlert) return "TOS Alert";
    return "Up to Date";
  };

  return (
    <InternPanel
      title="Apple Developer Agreements"
      subtitle="Agreement and TOS status"
      icon={<GavelIcon />}
      collapsible={false}
      actions={
        <Button
          variant="outlined"
          size="small"
          startIcon={<SyncIcon />}
          onClick={handleSync}
          disabled={isSyncInFlight}
        >
          {isSyncInFlight ? "Syncing..." : "Sync"}
        </Button>
      }
    >
      <Stack spacing={2}>
        {/* Status Row */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            p: 1.5,
            borderRadius: 1,
            bgcolor: "var(--intern-bg-secondary)",
            border: "1px solid var(--intern-border-default)",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Chip
              label={getStatusText()}
              color={getStatusColor()}
              size="small"
              sx={{ fontWeight: 500 }}
            />
            {hasPendingAgreements && (
              <Typography
                variant="caption"
                sx={{ color: "var(--intern-text-tertiary)" }}
              >
                Since{" "}
                {new Date(
                  brand.appleDeveloperAgreementsPendingSince!
                ).toLocaleDateString()}
              </Typography>
            )}
          </Stack>
        </Stack>

        {/* Alert */}
        {(hasPendingAgreements || showTosAlert) && (
          <Alert
            severity="warning"
            sx={{
              py: 0.5,
              "& .MuiAlert-message": { fontSize: "0.8125rem" },
            }}
          >
            {hasPendingAgreements
              ? "Agreements need attention. App submissions may be blocked."
              : "Review Apple's latest terms of service."}
          </Alert>
        )}

        {/* Success message when everything is up to date */}
        {!hasPendingAgreements && !showTosAlert && (
          <Typography
            variant="body2"
            sx={{ color: "var(--intern-text-tertiary)" }}
          >
            All Apple developer agreements are current.
          </Typography>
        )}
      </Stack>
    </InternPanel>
  );
};

export default InternAgreementsCard;
